import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import { white } from '@styles/colors';
import React, { useState } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import { bold, medium } from '@styles/fontWeights';
import NewHireNotificationModal from '../NewHireNotificationModal/NewHireNotificationModal';
import { NewHireConacts } from '@features/digitalOnboarding/types';
import { useGetNewHireConacts } from '@features/boostMyJobs/hooks/useGetNewHireConacts';
import { Add, Delete, Edit, Email } from '@mui/icons-material';
import PayrollPartnerDeleteModal from '../PayrollPartnerDeleteModal/PayrollPartnerDeleteModal';

function NewHireNotification({ companyId }: { companyId?: number }) {
  const [addNewHireNotificationModal, setAddNewHireNotificationModal] =
    useState(false);
  const [isDeletingModalOpen, setIsDeletingModalOpen] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const [newHireContectData, setNewHireContactData] =
    useState<NewHireConacts | null>(null);

  const { newHireContact, isLoadingData } = useGetNewHireConacts({
    ...(companyId ? { companyId } : {}),
    setErrorText,
  });

  return (
    <>
      <Box
        bgcolor={white}
        p={companyId ? 0 : 3}
        borderRadius={8}
        justifyContent="space-between"
        width={{ xs: '100%' }}
        height="fit-content"
      >
        {companyId && newHireContact?.objects?.length > 0 ? (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            bgcolor={white}
            py={3}
            width={1}
            borderRadius={8}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h3" fontWeight={medium}>
              New Hire Notifications
            </Typography>
            <Button
              onClick={() => setAddNewHireNotificationModal(true)}
              size="small"
              variant="contained"
              startIcon={<Add />}
            >
              Add New Hire Notification
            </Button>
          </Stack>
        ) : (
          <Stack spacing={3} alignItems="center">
            <EmailIcon sx={{ fontSize: '60px' }} />
            <Typography variant="h3" fontWeight={medium}>
              New Hire Notifications
            </Typography>
            <Typography
              maxWidth="530px"
              textAlign="center"
              color="text.secondary"
              lineHeight={1.2}
              fontWeight={medium}
            >
              Once the applicant has been hired or accepted the job offer,
              notifications should be sent to the contacts below
            </Typography>
          </Stack>
        )}
        {isLoadingData ? (
          <Box textAlign="center">
            <CircularProgress />
          </Box>
        ) : (
          newHireContact &&
          newHireContact.objects?.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Company</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Email</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newHireContact.objects.map((item: NewHireConacts) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="h6" sx={{ maxWidth: '400px', wordBreak: 'break-word', fontWeight: bold }}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ maxWidth: '400px', wordBreak: 'break-word' }}>
                          {item.company_name}
                        </Typography>
                        </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: '400px', wordBreak: 'break-word' }} display="flex" alignItems="center" gap={1}>
                          <Email
                            sx={{ fontSize: '14px', marginRight: '4px' }}
                          />
                          {item.email}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={2}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ minWidth: '90px' }}
                            startIcon={<Edit />}
                            onClick={() => {
                              setNewHireContactData(item);
                              setAddNewHireNotificationModal(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            sx={{ minWidth: '90px' }}
                            startIcon={<Delete />}
                            onClick={() => {
                              setNewHireContactData(item);
                              setIsDeletingModalOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
        {((companyId && newHireContact?.objects?.length <= 0) ||
          !companyId) && (
          <Box textAlign="center" mt={3}>
            <Button
              variant="contained"
              onClick={() => setAddNewHireNotificationModal(true)}
            >
              Add New Hire Contact
            </Button>
          </Box>
        )}
      </Box>
      {addNewHireNotificationModal ? (
        <NewHireNotificationModal
          isOpen={addNewHireNotificationModal}
          companyId={companyId}
          handleClose={() => {
            setAddNewHireNotificationModal(false);
            setNewHireContactData(null);
          }}
          newHireNotificationData={newHireContectData}
        />
      ) : null}
      <PayrollPartnerDeleteModal
        isOpen={isDeletingModalOpen}
        handleClose={() => {
          setIsDeletingModalOpen(false);
          setNewHireContactData(null);
        }}
        newHireContactData={newHireContectData}
        onboardingPackageData={null}
        newHireContact={true}
      />
    </>
  );
}

export default NewHireNotification;
